import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useI18n, SUPPORTED_LANGS } from "@/i18n/I18nProvider";
import Escudo from "@/components/site/Escudo";
import PeruRibbon from "@/components/site/PeruRibbon";

const NAV_ITEMS = [
  { key: "program", to: "#programa" },
  { key: "how_it_works", to: "#como-funciona" },
  { key: "advisors", to: "#especialistas" },
  { key: "faq", to: "#faq" },
];

export default function Header() {
  const { t, lang, switchLang, localePath } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const anchorHref = (hash) => `${localePath("/")}${hash}`.replace(/\/{2,}/g, "/").replace(":/", "://");

  const closeMenu = () => setOpen(false);

  return (
    <header className="w-full bg-white border-b border-[#0E2A47]/10" data-testid="site-header">
      <PeruRibbon height={3} />
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
        <NavLink
          to={localePath("/")}
          className="flex items-center gap-3"
          data-testid="header-brand-link"
          onClick={closeMenu}
        >
          <Escudo size={40} title={t("header.escudo_alt")} />
          <div className="flex flex-col leading-tight">
            <span className="font-serif text-[22px] tracking-tight text-[#0E2A47]">
              {t("brand.name")}
            </span>
            <span className="hidden md:block text-[11px] uppercase tracking-[0.16em] text-[#0E2A47]/60">
              {t("brand.tagline")}
            </span>
          </div>
        </NavLink>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8" aria-label="primary">
          <NavLink
            to={localePath("/")}
            end
            className={({ isActive }) =>
              `text-sm tracking-wide ${isActive ? "text-[#0E2A47] font-semibold" : "text-[#0E2A47]/75 hover:text-[#0E2A47]"}`
            }
            data-testid="nav-home-link"
          >
            {t("nav.home")}
          </NavLink>
          {NAV_ITEMS.map((item) => (
            <a
              key={item.key}
              href={anchorHref(item.to)}
              className="text-sm tracking-wide text-[#0E2A47]/75 hover:text-[#0E2A47]"
              data-testid={`nav-${item.key}-link`}
            >
              {t(`nav.${item.key}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1 rounded-full border border-[#0E2A47]/15 px-1 py-1" data-testid="lang-switcher">
            <Globe className="w-4 h-4 text-[#0E2A47]/70 ml-2" aria-hidden="true" />
            {SUPPORTED_LANGS.map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => switchLang(code)}
                className={`px-3 py-1 text-xs font-medium rounded-full ${
                  lang === code
                    ? "bg-[#0E2A47] text-white"
                    : "text-[#0E2A47] hover:bg-[#0E2A47]/5"
                }`}
                data-testid={`lang-btn-${code}`}
                aria-pressed={lang === code}
              >
                {code === "pe" ? "PE" : "EN"}
              </button>
            ))}
          </div>

          <a
            href={`${localePath("/")}#newsletter`}
            className="hidden md:inline-flex items-center px-5 py-2.5 rounded-full bg-[#0F5F3F] text-white text-sm font-medium hover:bg-[#0a4c32]"
            data-testid="header-cta-newsletter"
          >
            {t("nav.newsletter")}
          </a>

          <button
            type="button"
            className="lg:hidden p-2 text-[#0E2A47]"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? t("nav.menu_close") : t("nav.menu_open")}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-[#0E2A47]/10" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-3">
            <NavLink
              to={localePath("/")}
              end
              onClick={closeMenu}
              className="text-[#0E2A47] text-base py-2 border-b border-[#0E2A47]/10"
              data-testid="mobile-nav-home"
            >
              {t("nav.home")}
            </NavLink>
            {NAV_ITEMS.map((item) => (
              <a
                key={item.key}
                href={anchorHref(item.to)}
                onClick={closeMenu}
                className="text-[#0E2A47] text-base py-2 border-b border-[#0E2A47]/10"
                data-testid={`mobile-nav-${item.key}`}
              >
                {t(`nav.${item.key}`)}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-3">
              <span className="text-xs text-[#0E2A47]/60 mr-2">{t("language_switch.label")}:</span>
              {SUPPORTED_LANGS.map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => {
                    switchLang(code);
                    closeMenu();
                  }}
                  className={`px-3 py-1 text-xs font-medium rounded-full border ${
                    lang === code
                      ? "bg-[#0E2A47] text-white border-[#0E2A47]"
                      : "text-[#0E2A47] border-[#0E2A47]/25"
                  }`}
                  data-testid={`mobile-lang-btn-${code}`}
                >
                  {code === "pe" ? t("language_switch.es") : t("language_switch.en")}
                </button>
              ))}
            </div>
            <a
              href={`${localePath("/")}#newsletter`}
              onClick={closeMenu}
              className="mt-2 inline-flex justify-center items-center px-5 py-3 rounded-full bg-[#0F5F3F] text-white text-sm font-medium"
              data-testid="mobile-cta-newsletter"
            >
              {t("nav.newsletter")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
